import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Paper,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
} from "@mui/material";
import {
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  ExitToApp as ExitToAppIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { usageApi } from "../services/api";
import { keycloak } from "../services/keycloak";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrganizationList from "../components/OrganizationList";
import { changeLanguage, normalizeOptionalLanguage } from "../utils/language";
import { useTranslation } from "react-i18next";
import { cacheApi } from "@/services/cache-service";
import { usePlaygroundParametersStore } from "@/atoms/playground";

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Tab mapping for URL parameters
const TAB_MAP = {
  profile: 0,
  organizations: 1,
  security: 2,
};

const TAB_NAMES = ["profile", "organizations", "security"];

// Get Keycloak account URL
const getKeycloakAccountUrl = () => {
  const baseUrl = window.env.REACT_APP_KEYCLOAK_URL;
  const realm = window.env.REACT_APP_KEYCLOAK_REALM;
  if (baseUrl && realm) {
    return `${baseUrl}realms/${realm}/account`;
  }
  return null;
};

const Settings = () => {
  const { t } = useTranslation();

  const { currentUser, userProfile, setUserProfile, cache } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLanguage } = usePlaygroundParametersStore();

  // Initialize tab value based on URL parameter
  const getInitialTabValue = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam && TAB_MAP.hasOwnProperty(tabParam)) {
      return TAB_MAP[tabParam];
    }
    return 0; // Default to Profile tab
  };

  const [tabValue, setTabValue] = useState(getInitialTabValue);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [deletePaymentMethodDialogOpen, setDeletePaymentMethodDialogOpen] =
    useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [deletePaymentMethodLoading, setDeletePaymentMethodLoading] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const getSavedProfileLanguage = () => {
    if (userProfile?.language) {
      return normalizeOptionalLanguage(userProfile.language) || "";
    }

    const savedLanguage = cache?.preferences?.hasOwnProperty("language")
      ? cache.preferences.language
      : null;

    return normalizeOptionalLanguage(savedLanguage) || "";
  };

  const [profileForm, setProfileForm] = useState({
    displayName:
      currentUser?.displayName ||
      `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
      currentUser?.username ||
      "",
    email: currentUser?.email || "",
    photoURL: currentUser?.photoURL || "",
    organization: userProfile?.organization || "",
    language: getSavedProfileLanguage(),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const updatePreferences = async (language) => {
    try {
      setLoading(true);
      await cacheApi.updatePreferences({
        language,
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to update user preferences",
      );
    } finally {
      setLoading(false);
    }
  };

  // Update tab value when URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && TAB_MAP.hasOwnProperty(tabParam)) {
      const newTabValue = TAB_MAP[tabParam];
      if (newTabValue !== tabValue) {
        setTabValue(newTabValue);
      }
    }
  }, [searchParams, tabValue, paymentMethods.length]);

  useEffect(() => {
    if (isEditing) return;

    setProfileForm((prev) => ({
      ...prev,
      language: getSavedProfileLanguage(),
    }));
  }, [cache?.preferences, userProfile?.language, isEditing]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);

    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", TAB_NAMES[newValue]);
    setSearchParams(newSearchParams);
  };

  const fetchPaymentMethods = async () => {
    if (!currentUser) return;

    setPaymentMethodsLoading(true);
    try {
      const response = await usageApi.getPaymentMethods(currentUser.uid);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      toast.error(t("settings.failedToLoadPaymentMethods"));
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const handleDeletePaymentMethodClick = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setDeletePaymentMethodDialogOpen(true);
  };

  const handleDeletePaymentMethodCancel = () => {
    setDeletePaymentMethodDialogOpen(false);
    setSelectedPaymentMethod(null);
  };

  const handleDeletePaymentMethodConfirm = async () => {
    if (!selectedPaymentMethod || !currentUser) return;

    setDeletePaymentMethodLoading(true);
    try {
      await usageApi.deletePaymentMethod(
        currentUser.uid,
        selectedPaymentMethod.id,
      );
      toast.success(t("settings.paymentMethodDeletedSuccess"));
      fetchPaymentMethods();
    } catch (error) {
      console.error("Error deleting payment method:", error);
      toast.error(t("settings.failedToDeletePaymentMethod"));
    } finally {
      setDeletePaymentMethodLoading(false);
      setDeletePaymentMethodDialogOpen(false);
    }
  };

  const handleAddPaymentMethod = () => {
    // This would typically open a Stripe Elements form
    toast.info(t("toasts.paymentMethodInfo"));
  };

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      [name]: value,
    });
  };

  // Handle profile save
  const handleProfileSave = async () => {
    setIsSaving(true);
    try {
      if (currentUser) {
        const selectedLanguage = profileForm.language || null;
        // Since we're using Keycloak for user management, we only update the UI state
        // Actual profile updates should be done through Keycloak directly
        setUserProfile({
          ...userProfile,
          displayName: profileForm.displayName,
          photoURL: profileForm.photoURL,
          organization: profileForm.organization,
          language: selectedLanguage,
        });

        if (selectedLanguage) {
          setLanguage(selectedLanguage);
          await changeLanguage(selectedLanguage);
        }

        await updatePreferences(selectedLanguage);

        toast.success(t("settings.profileUpdatedSuccess"));
        setIsEditing(false);
      } else {
        toast.error(t("settings.mustBeLoggedIn"));
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(
        t("settings.failedToUpdateProfile", { message: error.message }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    try {
      // Log out from Keycloak
      keycloak.logout();

      toast.success(t("settings.loggedOutSuccess"));
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error(t("settings.failedToLogout"));
    } finally {
      setDeleteAccountLoading(false);
      setDeleteAccountDialogOpen(false);
    }
  };

  return (
    <DashboardLayout title={t("settings.title")}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
          >
            <Tab
              label={t("settings.profileTab")}
              icon={<PersonIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("settings.organizationsTab")}
              icon={<BusinessIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("settings.securityTab")}
              icon={<SecurityIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 2, sm: 0 },
                  mb: 3,
                }}
              >
                <Typography variant="h6">
                  {t("settings.profileInformation")}
                </Typography>
                {isEditing ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 1, sm: 1 },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Button
                      onClick={() => setIsEditing(false)}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "120px" },
                      }}
                      color="secondary"
                      variant="text"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleProfileSave}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "120px" },
                      }}
                      disabled={loading}
                    >
                      {t("settings.saveChanges")}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditing(true)}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: "120px" },
                    }}
                  >
                    {t("settings.editProfile")}
                  </Button>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("settings.displayName")}
                    name="displayName"
                    value={profileForm.displayName}
                    onChange={handleProfileChange}
                    fullWidth
                    disabled={!isEditing || isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("settings.email")}
                    name="email"
                    value={profileForm.email}
                    fullWidth
                    disabled={true} // Email can't be changed directly
                    margin="normal"
                    helperText={t("settings.emailCannotBeChanged")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("settings.profilePhotoUrl")}
                    name="photoURL"
                    value={profileForm.photoURL}
                    onChange={handleProfileChange}
                    fullWidth
                    disabled={!isEditing || isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("settings.organization")}
                    name="organization"
                    value={profileForm.organization}
                    onChange={handleProfileChange}
                    fullWidth
                    disabled={!isEditing || isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={t("settings.language")}
                    name="language"
                    value={profileForm.language}
                    onChange={handleProfileChange}
                    fullWidth
                    disabled={!isEditing || isSaving}
                    margin="normal"
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="" disabled aria-label="No language selected">
                      <span aria-hidden="true"> </span>
                    </MenuItem>
                    <MenuItem value="en">{t("settings.english")}</MenuItem>
                    <MenuItem value="ar">{t("settings.arabic")}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Account Deletion Section */}
          <Card
            sx={{
              mt: 4,
              bgcolor: (theme) =>
                theme.palette.mode === "light"
                  ? "#fee2e2" // Light red background for light mode
                  : "error.dark", // Dark red for dark mode
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "light"
                      ? "var(--color-52)" // Red text for light mode
                      : "white", // White text for dark mode
                }}
              >
                {t("settings.dangerZone")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "light"
                        ? "var(--color-64)" // Darker red text for light mode
                        : "white", // White text for dark mode
                    mb: 2,
                  }}
                >
                  {t("settings.dangerZoneDescription")}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setDeleteAccountDialogOpen(true)}
                  sx={{
                    borderColor: (theme) =>
                      theme.palette.mode === "light"
                        ? "var(--color-52)"
                        : "white",
                    color: (theme) =>
                      theme.palette.mode === "light"
                        ? "var(--color-52)"
                        : "white",
                    "&:hover": {
                      borderColor: (theme) =>
                        theme.palette.mode === "light"
                          ? "var(--color-52)"
                          : "white",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {t("settings.deleteAccount")}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Delete Account Dialog */}
          <Dialog
            open={deleteAccountDialogOpen}
            onClose={() => setDeleteAccountDialogOpen(false)}
          >
            <DialogTitle>{t("settings.deleteAccountTitle")}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t("settings.deleteAccountConfirm")}
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label={t("settings.typeDeleteToConfirm")}
                fullWidth
                variant="outlined"
                onChange={(e) => setDeleteConfirmation(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteAccountDialogOpen(false)}
                disabled={deleteAccountLoading}
                color="secondary"
                variant="text"
              >
                {t("common.cancel")}
              </Button>
              <Button
                color="error"
                onClick={handleDeleteAccount}
                disabled={
                  deleteAccountLoading || deleteConfirmation !== "DELETE"
                }
                startIcon={
                  deleteAccountLoading ? <CircularProgress size={20} /> : null
                }
              >
                {deleteAccountLoading
                  ? t("common.deleting")
                  : t("settings.deleteAccount")}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        {/* Organizations Tab */}
        <TabPanel value={tabValue} index={1}>
          <OrganizationList />
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("settings.securitySettings")}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* <Alert
                severity="info"
                sx={{
                  mb: 3,
                  fontSize: "0.95rem",
                }}
              >
                Your account is managed through Keycloak. To change your password or manage security settings,
                please use the Keycloak account management page.
              </Alert>*/}

              {/* {getKeycloakAccountUrl() && (
                <Button
                  variant="contained"
                  startIcon={<ExitToAppIcon />}
                  onClick={() => window.open(getKeycloakAccountUrl(), "_blank")}
                  sx={{ mb: 3 }}
                >
                  Manage Account in Keycloak
                </Button>
              )}*/}

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                {t("settings.accountInformation")}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("settings.username")}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser?.username || t("common.notAvailable")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("settings.emailVerified")}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser?.emailVerified
                      ? t("common.yes")
                      : t("common.no")}
                  </Typography>
                </Grid>
                {userProfile?.roles && userProfile.roles.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t("settings.roles")}
                    </Typography>
                    <Typography variant="body1">
                      {userProfile.roles.join(", ")}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Delete Payment Method Dialog */}
      <Dialog
        open={deletePaymentMethodDialogOpen}
        onClose={handleDeletePaymentMethodCancel}
      >
        <DialogTitle>{t("settings.deletePaymentMethod")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("settings.deletePaymentMethodConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeletePaymentMethodCancel}
            disabled={deletePaymentMethodLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeletePaymentMethodConfirm}
            color="error"
            disabled={deletePaymentMethodLoading}
            startIcon={
              deletePaymentMethodLoading ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deletePaymentMethodLoading
              ? t("common.deleting")
              : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Settings;
